import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  updateReportRow,
  createReportRow,
  deleteReportRow,
} from "../api/reportRow.api";
import {
  CreateReportRowPayload,
  UpdateReportRowPayload,
} from "../api/models/reportRow.models";
import { useTranslation } from "react-i18next";
import { CreateProductPayload } from "../api/models/product.models";
import { EcomodClass } from "../api/models/ecomodClass.models";
import { SupClass } from "../api/models/supClass.models";
import { convertAkeneoRowsToEcomodRows } from "../utils/rowUtils";
import { createProduct } from "../api/products.api";
import { CreatePackageRowPayload } from "../api/models/packageRow.models";
import { createNewPackageRow } from "../api/packageRow.api";

export enum ReportRowType {
  SIMPLE = "simple",
  ECOMOD = "ecomod",
}

const useReportRows = () => {
  const { t } = useTranslation();
  const [bearerToken, toast, companyId, organizationId] = useUIStore(
    useShallow((state: UIState) => [
      state.bearerToken,
      state.toastRef,
      state.companyId,
      state.organizationId,
    ]),
  );

  const queryClient = useQueryClient();

  const updateReportRowData = async (row: UpdateReportRowPayload) => {
    const result = await updateReportRowMutation.mutateAsync(row);
    return result;
  };

  const updateReportRowMutation = useMutation({
    mutationFn: (row: UpdateReportRowPayload) =>
      updateReportRow(
        companyId as number,
        { ...row, organizationId: organizationId || undefined },
        bearerToken,
      ),
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ["reports"],
      });
    },
    onError: (error) => {
      console.error("Failed to update report row:", error);
      toast?.current?.show({
        severity: "error",
        summary: t("header-row-error"),
        detail: t("text-failed-to-update-row"),
        life: 3000,
      });
    },
  });

  const createReportRowData = async (row: CreateReportRowPayload) => {
    const result = await createReportRowMutation.mutateAsync(row);
    return result;
  };

  const createReportRowMutation = useMutation({
    mutationFn: (row: CreateReportRowPayload) =>
      createReportRow(
        companyId as number,
        { ...row, organizationId: organizationId || undefined },
        bearerToken,
      ),
    onError: (error) => {
      console.error("Failed to create report row:", error);
      toast?.current?.show({
        severity: "error",
        summary: t("header-row-error"),
        detail: t("text-failed-to-create-row"),
        life: 3000,
      });
      queryClient.invalidateQueries({
        queryKey: ["products"],
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["reports"],
      });
    },
  });

  const deleteReportRowData = (rowId: number) => {
    deleteReportRowMutation.mutate(rowId);
  };

  const deleteReportRowMutation = useMutation({
    mutationFn: (rowId: number) =>
      deleteReportRow(
        companyId as number,
        rowId,
        organizationId || undefined,
        bearerToken,
      ),
    onError: (error) => {
      console.error("Failed to delete report row:", error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["reports"],
      });
    },
  });

  const createProductAndReportRow = async (
    packageRowPayload: CreatePackageRowPayload,
    productPayload: CreateProductPayload,
    rowData: CreateReportRowPayload,
    ecomodClasses?: EcomodClass[],
    supClasses?: SupClass[],
  ) => {
    // if the report is being updated to ecomod, update the report first

    // First create package
    const createdPackageRow =
      await createPackageRowMutation.mutateAsync(packageRowPayload);

    // Then create product
    const createdProduct = await createProductMutation.mutateAsync({
      ...productPayload,
      packageRowsAssociation: createdPackageRow.identifier,
    });

    // Then create report row
    const reportRowPayload: CreateReportRowPayload = {
      reportId: rowData.reportId,
      ean: createdProduct.ean,
      name: createdProduct.name,
      amount: rowData.amount,
      active: createdProduct.responsibleToReport || true,
      internalReference: createdProduct.internalReference,
      values: convertAkeneoRowsToEcomodRows(
        createdPackageRow.rowValues,
        ecomodClasses || [],
        supClasses || [],
      ),
    };

    const result = createReportRowMutation.mutateAsync(reportRowPayload, {
      onSuccess: () => {
        toast?.current?.show({
          severity: "success",
          detail: t("header-row-success"),
          life: 3000,
        });
        queryClient.invalidateQueries({ queryKey: ["reports"] });
      },
    });

    return result;
  };

  const createPackageRowMutation = useMutation({
    mutationFn: (packageRowPayload: CreatePackageRowPayload) =>
      createNewPackageRow(bearerToken as string, packageRowPayload),
    onError: (error) => {
      console.error("Failed to create package row:", error);
      toast?.current?.show({
        severity: "error",
        summary: t("header-row-error"),
        detail: t("text-failed-to-create-package-row"),
        life: 3000,
      });
    },
  });

  const createProductMutation = useMutation({
    mutationFn: (productPayload: CreateProductPayload) =>
      createProduct(bearerToken as string, productPayload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["searchProducts"] });
    },
    onError: (error) => {
      console.error("Failed to create product:", error);
      toast?.current?.show({
        severity: "error",
        summary: t("header-row-error"),
        detail: t("text-failed-to-create-product"),
        life: 3000,
      });
    },
  });

  const isUpdatingReportRow = updateReportRowMutation.isPending;
  const isCreatingReportRow = createReportRowMutation.isPending;
  const isDeletingReportRow = deleteReportRowMutation.isPending;

  return {
    createReportRowData,
    isCreatingReportRow,
    updateReportRowData,
    isUpdatingReportRow,
    deleteReportRowData,
    isDeletingReportRow,
    createProductAndReportRow,
  };
};

export default useReportRows;
